import api from '@/services/api';
import { getAccessToken } from '@/lib/session';
import { getServerUrl } from '@/lib/environment';

// http.ts already prefixes /api on the baseURL, so paths here start with /v2.
const BASE = '/v2/chats';

export type ChatOrigin = 'manual' | 'slack' | 'email' | 'api';
export type ChatStatus = 'active' | 'launched' | 'archived';

export interface Chat {
    _id: string;
    organizationId: string;
    userId: string;
    jobId: string | null;
    title: string;
    origin: ChatOrigin;
    originMeta: Record<string, unknown> | null;
    status: ChatStatus;
    lastMessageAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface ListChatsResponse {
    results: Chat[];
    total: number;
    page: number;
    limit: number;
}

export type ChatMessageRole = 'system' | 'user' | 'assistant' | 'tool';

export interface PersistedChatMessage {
    role: ChatMessageRole;
    content: string | null;
    tool_call_id?: string | null;
    tool_calls?: Array<{ id: string; type: 'function'; function: { name: string; arguments: string } }> | null;
    meta?: { ts?: string; kind?: string | null };
}

export interface ProfileArtifactPayload {
    markdown: string;
    summary: string;
    mustHaveSkills: string[];
    niceToHaveSkills: string[];
    experienceRange: { min?: number; max?: number } | null;
    seniorityLevel: string;
    industryBackground: string[];
    educationPreferences: string[];
    culturalFitSignals: string[];
    dealBreakers: string[];
}

export interface ProfileArtifact {
    version: number;
    summary: string;
    payload: ProfileArtifactPayload;
}

export interface EvaluationRequirement {
    id: string;
    name: string;
    description: string;
    stages: string[];
}

export interface EvaluationCriterion {
    id: string;
    name: string;
    weight: number;
    stages: string[];
}

export interface EvaluationPayload {
    requirements: EvaluationRequirement[];
    criteria: EvaluationCriterion[];
}

export interface PipelineStagePayload {
    id: string;
    name: string;
    order: number;
    stageKind: 'applied' | 'default' | 'agentic' | 'rejected';
    agentType?: 'cv_screener' | 'ai_interview' | 'sourcing' | 'nurturing' | null;
    isAutomated?: boolean;
    /** Optional projected funnel count for the demo's projection bar. */
    funnelCount?: number;
}

export interface PipelinePayload {
    name: string;
    stages: PipelineStagePayload[];
}

export interface SourceItem {
    id: string;
    /** Display key; not the canonical hiringSetup enum. */
    kind: 'linkedin' | 'indeed' | 'application-form' | 'sourcing-agent' | 'talent-pool' | 'referrals' | 'ats-import';
    label: string;
    description: string;
    enabled: boolean;
    badge?: string;
}

export interface SourcesPayload {
    items: SourceItem[];
}

export interface InterviewQuestion {
    id: string;
    text: string;
    kind: 'open' | 'scenario' | 'follow-up' | 'behavioural';
}

export interface InterviewSection {
    id: string;
    title: string;
    /** Two-digit ordinal display, e.g. "01". */
    ordinal: string;
    durationMinutes: number;
    questions: InterviewQuestion[];
}

export interface InterviewPayload {
    sections: InterviewSection[];
    totalMinutes: number;
}

export interface AutomationRulePayload {
    id: string;
    fromStage: string;
    toStage: string;
    trigger: string;
    mode: 'auto' | 'human';
    rationale: string;
}

export interface AutomationPayload {
    rules: AutomationRulePayload[];
}

export interface ArtifactBundle<T> {
    version: number;
    summary: string;
    payload: T;
}

export interface ChatArtifacts {
    profile: ProfileArtifact | null;
    evaluation: ArtifactBundle<EvaluationPayload> | null;
    pipeline: ArtifactBundle<PipelinePayload> | null;
    sources: ArtifactBundle<SourcesPayload> | null;
    interview: ArtifactBundle<InterviewPayload> | null;
    automation: ArtifactBundle<AutomationPayload> | null;
}

export type ArtifactKey = 'profile' | 'evaluation' | 'pipeline' | 'sources' | 'interview' | 'automation';

export interface LinkedJobSummary {
    title: string;
    location: string;
    employmentType: string;
    status: string;
    createdAt: string | null;
}

export interface ChatMessagesResponse {
    chatId: string;
    threadId: string | null;
    messages: PersistedChatMessage[];
    todoList: Array<{ id: string; content: string; status: 'pending' | 'in_progress' | 'completed' }>;
    pendingInterrupt: { toolCallId: string; schema: Record<string, unknown> } | null;
    jobId: string | null;
    chatStatus: ChatStatus;
    chatCreatedAt: string | null;
    chatOrigin: ChatOrigin;
    linkedJob: LinkedJobSummary | null;
    artifacts: ChatArtifacts;
}

export type SSEChatEventType =
    | 'agent_message'
    | 'tool_call'
    | 'todo_updated'
    | 'interrupt'
    | 'job_created'
    | 'job_launched'
    | 'launch_started'
    | 'followups_updated'
    | 'profile_updated'
    | 'evaluation_updated'
    | 'pipeline_updated'
    | 'sources_updated'
    | 'interview_updated'
    | 'automation_updated'
    | 'thinking_started'
    | 'thinking_chunk'
    | 'thinking_done'
    | 'benchmark_cohort'
    | 'graph_step'
    | 'error'
    | 'done';

export interface LaunchSequenceStep {
    id: string;
    text: string;
    kind?: 'check' | 'pending';
}

export interface LaunchSequenceData {
    id: string;
    label: string;
    expanded?: boolean;
    steps: LaunchSequenceStep[];
    title: string;
    jobUrl: string;
    slackChannel: string;
    hiringTeamNames: string[];
    expectations: string[];
}

export type InterruptSchema =
    | { type: 'clarification'; question: string; placeholder?: string }
    | { type: 'choice'; question: string; options: { value: string; label: string }[]; allowOther?: boolean }
    | {
          type: 'questions';
          questions: Array<{ id: string; question: string; choices?: { value: string; label: string }[] }>;
      };

export interface InterruptPayload {
    toolCallId: string;
    schema: InterruptSchema;
}

export interface SSEChatEvent {
    event: SSEChatEventType;
    // Loose typing — phases beyond 3 will refine per-event shapes.
    data: Record<string, unknown>;
}

export interface BenchmarkProfileDetail {
    name: string | null;
    location: string | null;
    headline: string;
    summary: string;
    yearsExperience: number | null;
    experience: Array<{
        company: string | null;
        title: string | null;
        startDate?: string | null;
        endDate?: string | null;
        isCurrent: boolean;
        description?: string;
    }>;
    education: Array<{ school?: string; degree?: string; field?: string }>;
    skills: string[];
    detailLoaded: true;
}

export const chatsApi = {
    /**
     * Lazy-fetch the rich detail panel for a single benchmark cohort
     * profile. Called when the user clicks a row in the cohort card to
     * expand it. Server runs an Exa crawl + fast-model extraction and
     * caches the result on the chat doc, so a second click on the same
     * row returns instantly.
     */
    async fetchBenchmarkProfileDetail(params: { url: string; chatId?: string }) {
        const { data } = await api.post<{ ok: boolean; detail: BenchmarkProfileDetail | null; error?: string }>(
            '/v2/role-setup/benchmark-profile-detail',
            params,
        );
        return data;
    },
    async list(params: { page?: number; limit?: number; includeArchived?: boolean } = {}) {
        const { data } = await api.get<ListChatsResponse>(BASE, { params });
        return data;
    },
    async get(chatId: string) {
        const { data } = await api.get<{ chat: Chat }>(`${BASE}/${chatId}`);
        return data.chat;
    },
    async create(
        payload: {
            origin?: ChatOrigin;
            originMeta?: Record<string, unknown>;
            initialMessage?: string;
        } = {},
    ) {
        const { data } = await api.post<{ chat: Chat }>(BASE, payload);
        return data.chat;
    },
    async archive(chatId: string) {
        const { data } = await api.delete<{ ok: boolean }>(`${BASE}/${chatId}`);
        return data;
    },
    async getMessages(chatId: string) {
        const { data } = await api.get<ChatMessagesResponse>(`${BASE}/${chatId}/messages`);
        return data;
    },
};

/**
 * Stream a chat turn from the role-setup agent.
 *
 * Returns an AbortController so callers can cancel mid-flight (e.g. on
 * unmount or "Stop" button). Errors are surfaced through the onEvent
 * callback as `event: 'error'`.
 */
export function sendChatMessage(
    chatId: string,
    message: string,
    onEvent: (event: SSEChatEvent) => void,
): AbortController {
    const controller = new AbortController();

    (async () => {
        try {
            const token = getAccessToken();
            // `getServerUrl()` already includes `/api`; do NOT prepend it again.
            const response = await fetch(`${getServerUrl()}${BASE}/${chatId}/messages`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({ message }),
                signal: controller.signal,
                credentials: 'include',
            });

            if (!response.ok) {
                const err = await response.json().catch(() => ({ error: 'Request failed' }));
                onEvent({ event: 'error', data: { message: err.error || 'Request failed' } });
                return;
            }

            const reader = response.body?.getReader();
            if (!reader) return;

            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                let currentEvent = '';
                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    } else if (line.startsWith('data: ') && currentEvent) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            onEvent({ event: currentEvent as SSEChatEventType, data });
                        } catch {
                            // skip malformed data
                        }
                        currentEvent = '';
                    }
                }
            }
        } catch (err) {
            if ((err as Error)?.name === 'AbortError') return;
            onEvent({ event: 'error', data: { message: (err as Error)?.message || 'Stream failed' } });
        }
    })();

    return controller;
}

/**
 * Resume a paused (interrupted) run after the user answers the agent's
 * `ask_user` form.
 */
export function resumeChatInterrupt(
    chatId: string,
    threadId: string,
    value: unknown,
    onEvent: (event: SSEChatEvent) => void,
): AbortController {
    const controller = new AbortController();

    (async () => {
        try {
            const token = getAccessToken();
            const response = await fetch(
                `${getServerUrl()}${BASE}/${chatId}/thread/${threadId}/resume`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({ value }),
                    signal: controller.signal,
                    credentials: 'include',
                },
            );

            if (!response.ok) {
                const err = await response.json().catch(() => ({ error: 'Request failed' }));
                onEvent({ event: 'error', data: { message: err.error || 'Request failed' } });
                return;
            }

            const reader = response.body?.getReader();
            if (!reader) return;

            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value: chunk } = await reader.read();
                if (done) break;
                buffer += decoder.decode(chunk, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                let currentEvent = '';
                for (const line of lines) {
                    if (line.startsWith('event: ')) {
                        currentEvent = line.slice(7).trim();
                    } else if (line.startsWith('data: ') && currentEvent) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            onEvent({ event: currentEvent as SSEChatEventType, data });
                        } catch {
                            // skip malformed
                        }
                        currentEvent = '';
                    }
                }
            }
        } catch (err) {
            if ((err as Error)?.name === 'AbortError') return;
            onEvent({ event: 'error', data: { message: (err as Error)?.message || 'Stream failed' } });
        }
    })();

    return controller;
}
